import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { useAsyncJob } from '../../hooks/useAsyncJob';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
    color: '#4CAF50'
  }
}));

const InsightCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
  }
}));

const RFPAnalysisPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  const [rfpInsights, setRfpInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const { startPolling } = useAsyncJob();
  const [error, setError] = useState(null);
  const [hasRfpDocuments, setHasRfpDocuments] = useState(false);
  const [rfpDocuments, setRfpDocuments] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (project) {
      checkForRfpDocuments();
      loadRfpInsights();
    }
  }, [project]);

  const checkForRfpDocuments = () => {
    if (project?.documents?.rfp) {
      setHasRfpDocuments(project.documents.rfp.length > 0);
      setRfpDocuments(project.documents.rfp);
    } else {
      setHasRfpDocuments(false);
      setRfpDocuments([]);
    }
  };

  const loadRfpInsights = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const response = await fetch(`/api/projects/${projectId}/rfp-insights`);
      if (response.ok) {
        const data = await response.json();
        setRfpInsights(data.insights);
      }
    } catch (error) {
      console.error('Error loading RFP insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRfpInsights = async () => {
    if (!projectId || !hasRfpDocuments) return;

    try {
      setAnalyzing(true);
      setError(null);

      // Backend returns job_id immediately; we poll for completion
      const response = await fetch(`/api/projects/${projectId}/rfp-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ analyze: true, documents: rfpDocuments.map(doc => doc.document_id) }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to start RFP analysis');
        return;
      }

      const data = await response.json();

      if (data.job_id) {
        // Async path — poll until done
        setSnackbarMessage('RFP analysis running in background — usually 1-3 minutes for scanned PDFs...');
        setSnackbarOpen(true);
        const result = await startPolling(data.job_id, null, 'Analyzing RFP document…');
        if (result?.success && result?.insights) {
          setRfpInsights(result.insights);
          setSnackbarMessage('RFP insights generated successfully!');
          setSnackbarOpen(true);
        } else {
          setError(result?.error || 'RFP analysis returned no data');
        }
      } else if (data.insights) {
        // Legacy sync path
        setRfpInsights(data.insights);
        setSnackbarMessage('RFP insights generated successfully!');
        setSnackbarOpen(true);
      } else {
        setError(data.message || 'Failed to generate RFP insights');
      }
    } catch (error) {
      console.error('Error generating RFP insights:', error);
      setError(error.message || 'An error occurred while generating insights');
    } finally {
      setAnalyzing(false);
    }
  };

  const getInsightIcon = (section) => {
    switch (section) {
      case 'business_requirements': return <BusinessIcon />;
      case 'technical_requirements': return <BuildIcon />;
      case 'timeline_milestones': return <ScheduleIcon />;
      case 'budget_resources': return <AttachMoneyIcon />;
      case 'evaluation_criteria': return <AssessmentIcon />;
      case 'risk_factors': return <WarningIcon />;
      default: return <FindInPageIcon />;
    }
  };

  const getSectionTitle = (section) => {
    switch (section) {
      case 'executive_summary': return 'Executive Summary';
      case 'business_requirements': return 'Business Requirements';
      case 'technical_requirements': return 'Technical Requirements';
      case 'timeline_milestones': return 'Timeline & Milestones';
      case 'budget_resources': return 'Budget & Resources';
      case 'evaluation_criteria': return 'Evaluation Criteria';
      case 'risk_factors': return 'Risk Factors';
      default: return section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  if (!hasRfpDocuments) {
    return (
      <Box>
        <SectionTitle variant="h4">
          <FindInPageIcon />
          RFP Analysis
        </SectionTitle>
        
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<DocumentScannerIcon />}
        >
          <Typography variant="h6" gutterBottom>
            No RFP Documents Found
          </Typography>
          <Typography variant="body2">
            This project doesn't have any RFP (Request for Proposal) documents uploaded. 
            To use AI-powered RFP analysis, please upload RFP documents when creating or editing the project.
          </Typography>
        </Alert>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            How to Add RFP Documents
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Go to the Admin Panel or Projects page"
                secondary="Use the project creation or editing forms"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Upload your RFP document"
                secondary="Supported formats: PDF, DOCX, DOC, TXT, MD"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Return to this page for AI analysis"
                secondary="Our AI will extract insights from your RFP documents"
              />
            </ListItem>
          </List>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle variant="h4">
        <FindInPageIcon />
        RFP Analysis
      </SectionTitle>

      {/* RFP Documents Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          RFP Documents ({rfpDocuments.length})
        </Typography>
        <Grid container spacing={2}>
          {rfpDocuments.map((doc, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" noWrap>
                    {doc.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Document ID: {doc.document_id}
                  </Typography>
                  <Chip 
                    label="RFP Document" 
                    size="small" 
                    color="primary" 
                    sx={{ mt: 1 }} 
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Analysis Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              AI-Powered RFP Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate comprehensive insights from your RFP documents using advanced AI analysis
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={analyzing ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              onClick={generateRfpInsights}
              disabled={analyzing || !hasRfpDocuments}
              sx={{ mr: 1 }}
            >
              {analyzing ? 'Analyzing...' : 'Generate Insights'}
            </Button>
            {rfpInsights && (
              <IconButton onClick={loadRfpInsights} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <Typography variant="subtitle2">Analysis Error</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Loading State */}
      {(loading || analyzing) && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* RFP Insights Display */}
      {rfpInsights && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Analysis Results
          </Typography>
          
          {Object.entries(rfpInsights).map(([section, content]) => (
            <Accordion key={section} defaultExpanded={section === 'executive_summary'}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${section}-content`}
                id={`${section}-header`}
              >
                <Box display="flex" alignItems="center">
                  {getInsightIcon(section)}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {getSectionTitle(section)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {Array.isArray(content) ? (
                    <List>
                      {content.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {content}
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default RFPAnalysisPage;